import { Component, OnInit, inject, ViewChild, TemplateRef, signal, AfterViewInit, computed } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { Router } from '@angular/router'
import { RoutePaths } from 'src/app/core/data/route-paths.data'
import { ConfigurablePageComponent } from 'src/app/core/abstracts/configurable-page.component'
import { IAsset } from 'src/app/core/interfaces/asset.interface'
import { EventService } from 'src/app/core/services/event.service'
import { GlobalDataService } from 'src/app/core/services/global-data.service'
import { SeoService } from 'src/app/core/services/seo.service'
import { CommonModule } from '@angular/common'
import { MatExpansionModule } from '@angular/material/expansion'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { BrowseByCategoryComponent } from 'src/app/components/browse-by-category/browse-by-category.component'
import { BrowseByMakeComponent } from 'src/app/components/browse-by-make/browse-by-make.component'
import { ContactFormComponent } from 'src/app/components/contact-form/contact-form.component'
import { FeaturedCarouselComponent } from 'src/app/components/featured-carousel/featured-carousel.component'
import { FooterComponent } from 'src/app/components/footer/footer.component'
import { HeaderComponent } from 'src/app/components/header/header.component'
import { HeroBannerComponent } from 'src/app/components/hero-banner/hero-banner.component'
import { SectionComponent } from 'src/app/components/section/section.component'
import { ColorSchemeDirective } from 'src/app/core/directives/color-scheme.directive'
import { defaultLayoutOrder } from 'src/app/core/data/home-page.data'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { debounceTime } from 'rxjs'
import { extractComponentType } from 'src/app/core/utils/component.util'

interface ITemplateMap {
  [key: string]: TemplateRef<any> | null
}

interface IOrderableComponent {
  instanceKey: string
  componentType: string
  config: any
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['../../../assets/page.scss', './home.component.scss'],
  providers: [SeoService],
  imports: [
    CommonModule,
    MatExpansionModule,
    MatSlideToggleModule,
    HeaderComponent,
    FooterComponent,
    SectionComponent,
    ContactFormComponent,
    BrowseByCategoryComponent,
    BrowseByMakeComponent,
    HeroBannerComponent,
    FeaturedCarouselComponent,
    ColorSchemeDirective,
  ],
})
export class HomeComponent extends ConfigurablePageComponent implements OnInit, AfterViewInit {
  @ViewChild('heroBannerTemplate') heroBannerTemplate!: TemplateRef<any>
  @ViewChild('featuredCarouselTemplate') featuredCarouselTemplate!: TemplateRef<any>
  @ViewChild('browseByCategoryTemplate') browseByCategoryTemplate!: TemplateRef<any>
  @ViewChild('browseByMakeTemplate') browseByMakeTemplate!: TemplateRef<any>
  @ViewChild('contactFormSectionTemplate') contactFormSectionTemplate!: TemplateRef<any>

  private _router = inject(Router)
  private _seoService = inject(SeoService)
  private _eventService = inject(EventService)
  protected globalData = inject(GlobalDataService)

  contactForm = new FormGroup({})

  private _viewInitialized = signal(false)

  private templateMap = computed<ITemplateMap>(() => {
    if (!this._viewInitialized()) {
      return {
        heroBanner: null,
        featuredCarousel: null,
        browseByCategory: null,
        browseByMake: null,
        contactFormSection: null
      }
    }
    return {
      heroBanner: this.heroBannerTemplate,
      featuredCarousel: this.featuredCarouselTemplate,
      browseByCategory: this.browseByCategoryTemplate,
      browseByMake: this.browseByMakeTemplate,
      contactFormSection: this.contactFormSectionTemplate
    }
  })

  orderedComponents = signal<IOrderableComponent[]>([])

  constructor() {
    super()
    this._eventService.configUpdated$.pipe(
      takeUntilDestroyed(),
      debounceTime(200),
    ).subscribe(() => {
      this.generateOrderedComponents()
    })
  }

  generateOrderedComponents() {
    const config = this.globalDataService.config().page.home
    const order = config.layout?.order || [...defaultLayoutOrder]
    const orderedComps = order.map(instanceKey => ({
      instanceKey,
      componentType: extractComponentType(instanceKey),
      config: config.components[instanceKey]
    }))
    this.orderedComponents.set(orderedComps)
  }

  getTemplate(componentType: string): TemplateRef<any> | null {
    const templates = this.templateMap()
    return templates[componentType] || null
  }

  ngOnInit() {
    const config = this.globalDataService.config()
    this.pageConfig.set(config.page.home)
    this._seoService.init(
      config.seo.data,
      this.pageConfig().seo.data,
    )
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit()
    this._viewInitialized.set(true)
    this.generateOrderedComponents()
  }

  cardClicked(assetId: string) {
    this._router.navigate([RoutePaths.Details, assetId])
  }

  contactClicked(asset: IAsset) {
    this._eventService.notify('contactClicked', asset)
  }
}
